import React, { useEffect, useState } from 'react';
import { Alert, Button, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import { cleanDateInput, dateOrNull, errorText } from '../../utils/e2i';

/**
 * A confirmation with an optional comment (approve, reject, send back, cancel with a reason, return with a note)
 * and an optional date (the client submission date). onConfirm may throw: its message is shown here.
 */
export interface ActionDialogProps {
  open: boolean;
  id: string;
  title: string;
  help?: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  /** Shows the comment box with this label. */
  commentLabel?: string;
  commentRequired?: boolean;
  /** Shows a date box with this label. */
  dateLabel?: string;
  dateRequired?: boolean;
  initialDate?: string;
  maxDate?: string;
  onClose: () => void;
  onConfirm: (values: { comment: string; date: string | null }) => Promise<void>;
}

const ActionDialog: React.FC<ActionDialogProps> = ({
  open, id, title, help, confirmLabel, danger, commentLabel, commentRequired, dateLabel, dateRequired, initialDate, maxDate, onClose, onConfirm,
}) => {
  const [comment, setComment] = useState('');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setComment('');
    setDate(initialDate || '');
    setBusy(false);
    setError(null);
  }, [open, initialDate]);

  const dateValue = dateOrNull(date);
  const blocked = (commentRequired && !comment.trim()) || (dateRequired && !dateValue) || (!!date.trim() && !dateValue);

  const confirm = async () => {
    if (blocked) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm({ comment: comment.trim(), date: dateValue });
    } catch (e: any) {
      setError(errorText(e, 'The action failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby={`${id}-title`}>
      <DialogTitle id={`${id}-title`}>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {help ? (typeof help === 'string' ? <Typography variant="body2">{help}</Typography> : help) : null}
          {dateLabel ? (
            <TextField
              id={`${id}-date`}
              type="date"
              label={dateLabel}
              value={date}
              onChange={(e) => setDate(cleanDateInput(e.target.value))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: maxDate || undefined }}
              required={dateRequired}
              error={!!date.trim() && !dateValue}
              helperText={!!date.trim() && !dateValue ? 'Not a date from 2000 to 2099' : undefined}
              sx={{ maxWidth: 320 }}
            />
          ) : null}
          {commentLabel ? (
            <TextField
              id={`${id}-comment`}
              label={commentRequired ? `${commentLabel} (required)` : `${commentLabel} (optional)`}
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 1000))}
              multiline
              minRows={2}
              fullWidth
              required={commentRequired}
            />
          ) : null}
          {error ? <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Back</Button>
        <Button color={danger ? 'error' : 'primary'} onClick={() => { void confirm(); }} disabled={busy || blocked}>
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ActionDialog;
